import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import { errorHandler, showMessage } from 'shared/feedback/messages';
import { i18n } from 'shared/i18n';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { EventType, dispatchMessage } from 'shared/feedback/toasts';
import { documentKeys } from './keys';
import type { paths } from 'shared/api/generated/api';

type DocumentResponse = NonNullable<paths['/documents/{revisionId}']['get']['responses'][200]['content']['application/json']>;

export interface ClassMetadataEntry {
  elements: string[];
  labelSv: string;
  labelEn: string;
  comment?: string;
  customCss?: string;
  boldFontFamily?: string;
  italicFontFamily?: string;
  toolbars?: string[];
  scopes?: string[];
}

export type DesignSlotType = 'graphical-variables' | 'paper' | 'form-classes' | 'font' | 'document' | 'template' | 'page-variables' | 'builtin-css';

interface DesignEntryBase {
  styleId: string;
  pageId?: string;
  templateSuperId?: string;
  templateRevisionId?: string;
  templateId?: string;
}

export type DesignEntry =
  | (DesignEntryBase & { type: 'graphical-variables' | 'paper' | 'form-classes' | 'font' | 'document' | 'page-variables'; styleId: string })
  | (DesignEntryBase & { type: 'template'; styleId: string })
  | (DesignEntryBase & { type: 'builtin-css'; slots?: Record<string, string> })
  | (DesignEntryBase & { type: string; [key: string]: unknown });

export interface FormClassesToolbarEntries {
  toolbar?: string;
  financialToolbar?: string;
}
export interface FormClassesScopeEntries {
  pageClasses?: string;
  tableClasses?: string;
}

export interface DesignStyle {
  id?: string;
  revisionId?: string;
  content?: string | Record<string, string> | Record<string, Record<string, string>>;
  metadata?: Record<string, ClassMetadataEntry>;
  builtinSlots?: Record<string, string>;
  toolbarEntries?: FormClassesToolbarEntries;
  scopeEntries?: FormClassesScopeEntries;
  [key: string]: unknown;
}

export interface ReferenceCell {
  value?: string;
  readOnly?: boolean;
  locales?: Record<string, string>;
  format?: string;
  classes?: string[];
  background?: string;
  border?: string;
  styling?: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface ReferenceDataValue {
  id?: string | number;
  name?: string;
  superId?: string;
  revisionId?: string;
  fileSuperId?: string;
  fileRevisionId?: string;
  locale?: string;
  createdAt?: string;
  updatedAt?: string;
  selection?: unknown;
  selectionContent?: ReferenceCell[][];
  [key: string]: unknown;
}

export type DraftStyleEntry =
  | { type: 'graphical-variables' | 'paper'; style: Record<string, string> | string }
  | {
      type: 'form-classes';
      style: Record<string, Record<string, string>>;
      metadata?: Record<string, ClassMetadataEntry>;
      rawScss?: string;
      toolbarComment?: string;
      scopeComment?: string;
      builtinSlots?: Record<string, string>;
    }
  | { type: 'builtin-css'; slots: Record<string, string> }
  | { type: 'document'; style: string; templateSuperId?: string; templateRevisionId?: string }
  | { type: 'template'; style: string; structure?: unknown; templateSuperId?: string; superId?: string }
  | { type: string; [key: string]: unknown };

export interface SectionTargetLanguage {
  superId: string;
  revisionId: string;
  locale?: string;
  name?: string;
  [key: string]: unknown;
}

export interface SectionEntry {
  superId: string;
  revisionId: string;
  locale?: string;
  name?: string;
  targetLanguages?: SectionTargetLanguage[];
  [key: string]: unknown;
}

export interface ReferenceDataEntry {
  superId: string;
  revisionId: string;
  locale?: string;
  cssClass?: string;
  [key: string]: unknown;
}

export interface ImageEntry {
  superId: string;
  revisionId: string;
  [key: string]: unknown;
}

export interface PageArea {
  type: string;
  options?: {
    pageNumber?: boolean;
    headers?: boolean;
    text?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PageNumbering {
  showFrom: number;
  countFrom: number;
  [key: string]: unknown;
}

export interface DocumentMetadata {
  texts?: Record<string, Record<string, string>>;
  [key: string]: unknown;
}

export type EditorDocument = DocumentResponse & {
  content: {
    locale?: string;
    sections: SectionEntry[];
    design: DesignEntry[];
    referenceData: ReferenceDataEntry[];
    images: ImageEntry[];
    fonts?: ImageEntry[];
    header?: PageArea | null;
    footer?: PageArea | null;
    pageNumbering?: PageNumbering;
    metadata?: DocumentMetadata;
    [key: string]: unknown;
  };
};

export const useEditorDocumentQuery = ({
  documentId,
  documentRevisionId,
  useDraft = false,
  withStyle,
  showErrors = true,
}: {
  documentId: number | string;
  documentRevisionId?: string;
  useDraft?: boolean;
  withStyle?: unknown;
  showErrors?: boolean;
}) =>
  useQuery({
    queryKey: documentKeys.editorDocument(documentId, documentRevisionId, useDraft, withStyle),

    queryFn: async (): Promise<EditorDocument | undefined> => {
      const sassQuery = {
        compileSass: 'true',
        ...(useDraft ? { useDraft: 'true' } : {}),
        ...(withStyle ? { withStyle: String(withStyle) } : {}),
      };
      if (documentRevisionId) {
        const { data } = await api.GET('/documents/{revisionId}', { params: { path: { revisionId: documentRevisionId }, query: sassQuery } });
        return data;
      }
      const { data } = await api.GET('/documents/', { params: { query: { superId: Number(documentId), current: 'true', ...sassQuery } } });
      const documents = data;
      return documents?.length ? documents[documents.length - 1] : undefined;
    },

    enabled: !!documentId || !!documentRevisionId,
    refetchOnWindowFocus: false,
    ...(showErrors ? {} : { meta: { errorHandler: () => {} } }),
  });

export const useEditorSection = (sectionRevisionId?: string, showErrors = true) =>
  useQuery({
    queryKey: documentKeys.editorSection(sectionRevisionId ?? ''),

    queryFn: async () => {
      const { data } = await api.GET('/sections/{revisionId}', { params: { path: { revisionId: sectionRevisionId as string } } });
      return data;
    },

    enabled: !!sectionRevisionId,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    ...(showErrors ? {} : { meta: { errorHandler: () => {} } }),
  });

export const useClonePreview = (documentSuperId: number | string, documentRevisionId?: string) =>
  useQuery({
    queryKey: ['CLONE_PREVIEW', documentSuperId, documentRevisionId],

    queryFn: () =>
      api
        .GET('/documents/{superId}/clone-preview', {
          params: { path: { superId: Number(documentSuperId) }, query: { revisionId: documentRevisionId as string } },
        })
        .then((response) => response.data),

    enabled: !!(documentSuperId && documentRevisionId),
  });

export type DocumentCssClasses = NonNullable<paths['/documents/{superId}/css-classes']['get']['responses'][200]['content']['application/json']>;

export const fetchDocumentCssClasses = (documentSuperId: number | string): Promise<DocumentCssClasses> =>
  api.GET('/documents/{superId}/css-classes', { params: { path: { superId: Number(documentSuperId) } } }).then((response) => response.data);

export const useSectionReorder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, sectionId, sectionOrder }: { documentId: number | string; sectionId: string; sectionOrder: unknown }) =>
      api
        .POST('/documents/{superId}/actions', {
          params: { path: { superId: Number(documentId) }, query: { compileSass: 'true' } },
          body: { action: 'set_section_order', data: { sectionId, sectionOrder } },
        })
        .catch((e) => {
          errorHandler(e);
          throw new Error('');
        }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
    },
  });
};

type TocQuery = NonNullable<paths['/documents/{revisionId}/table-of-content']['get']['parameters']['query']>;
interface TocOptions extends Partial<Omit<TocQuery, 'headerHierarchy'>> {
  headerHierarchy?: string[];
}

export const useGetDocumentTableOfContent = (
  revisionId: string | null,
  { start, stop, currentPosition, sectionSuperId, headerHierarchy, locale, sectionRevisionId }: TocOptions = {}
) => {
  const headers = headerHierarchy || ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  return useQuery({
    queryKey: documentKeys.toc({ revisionId, locale, start, stop, currentPosition, sectionSuperId, headerHierarchy, sectionRevisionId }),

    queryFn: () => {
      if (revisionId === null) {
        return Promise.resolve(null);
      }
      return api
        .GET('/documents/{revisionId}/table-of-content', {
          params: {
            path: { revisionId },
            query: { start, stop, currentPosition, sectionSuperId, locale, sectionRevisionId, headerHierarchy: headers.join(',') },
          },
        })
        .then((res) => res.data);
    },

    placeholderData: keepPreviousData,
  });
};

export const useCreateFontStyle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentSuperId, fileSuperId }: { documentSuperId: number | string; fileSuperId: string }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'create_font_style', data: { fileSuperId } },
      }),

    onSuccess: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('project-details:font-created.success'),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
    },

    onError: (error: unknown) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('project-details:font-created.error'),
        removeTime: 4000,
      });
      errorHandler(error);
    },
  });
};

export const useCloneDocument = () =>
  useMutation({
    mutationFn: ({
      documentSuperId,
      documentRevisionId,
      selectedOrganization,
      rollForward,
    }: {
      documentSuperId: number | string;
      documentRevisionId?: string;
      selectedOrganization?: unknown;
      rollForward?: unknown;
    }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: {
          action: 'clone_document',
          organization: selectedOrganization,
          revisionId: documentRevisionId,
          superId: documentSuperId,
          ...(rollForward ? { rollForward } : {}),
        },
      }),

    onSuccess: () => {
      showMessage('success', i18n('project-details:document-copy-success'));
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });

export const usePageNumberingSave = () =>
  useMutation({
    mutationFn: ({
      documentSuperId,
      documentRevisionId,
      pageNumbering,
    }: {
      documentSuperId: number | string;
      documentRevisionId?: string;
      pageNumbering: unknown;
    }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'update_document_page_numbering', data: { revisionId: documentRevisionId, superId: documentSuperId, pageNumbering } },
      }),

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });

export const useDocumentAreaSave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentSuperId,
      documentRevisionId,
      header,
      footer,
    }: {
      documentSuperId: number | string;
      documentRevisionId?: string;
      header?: unknown;
      footer?: unknown;
    }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'update_document_area', data: { revisionId: documentRevisionId, superId: documentSuperId, header, footer } },
      }),

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n(`document:area.${variables.footer ? 'footer' : 'header'}.save-success`),
        removeTime: 4000,
      });
      queryClient.invalidateQueries({ queryKey: [...documentKeys.detailAll] });
    },

    onError: (error, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n(`document:area.${variables.footer ? 'footer' : 'header'}.save-error`),
        removeTime: 4000,
      });
      errorHandler(error);
    },
  });
};

export const useDocumentMetadataSave = () =>
  useMutation({
    mutationFn: ({ documentSuperId, documentRevisionId, metadata }: { documentSuperId: number | string; documentRevisionId?: string; metadata: unknown }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'update_document_metadata', data: { revisionId: documentRevisionId, superId: documentSuperId, metadata } },
      }),

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });

export const useChangeDocumentName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentSuperId, newName }: { documentSuperId: number | string; newName: string }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'update_document_name', newName },
      }),

    onSuccess: () => {
      showMessage('success', i18n('project-list:document-rename-success'));
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.listAll],
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentSuperId, selectedOrganization }: { documentSuperId: number | string; selectedOrganization?: unknown }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'delete_document', organization: selectedOrganization },
      }),

    onSuccess: () => {
      showMessage('success', i18n('project-list:document-delete-success'));
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.listAll],
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
};

export const useRestoreDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentSuperId, selectedOrganization }: { documentSuperId: number | string; selectedOrganization?: unknown }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'restore_document', organization: selectedOrganization },
      }),

    onSuccess: () => {
      showMessage('success', i18n('project-list:document-restore-success'));
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.listAll],
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
};

export const useImportableDocuments = ({ organization }: { organization?: unknown }) => {
  return useQuery({
    queryKey: [],

    queryFn: async () => {
      const { data } = await api.GET('/documents/importable/{organization}', { params: { path: { organization: String(organization) } } });
      return (data as { files?: string[] })?.files;
    },

    enabled: !!organization,
  });
};

export function useNewDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentName, selectedOrganization }: { documentName: string; selectedOrganization: unknown }) =>
      api.POST('/documents/', { body: { organization: String(selectedOrganization), name: documentName } }),

    onSuccess: () => {
      showMessage('success', i18n('project-details:document-create-success'));
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.listAll],
      });
    },

    onError: (error: unknown) => {
      errorHandler(error);
    },
  });
}

export const useDocumentChanges = (documentId: number | string, fromDate: unknown, toDate: unknown) =>
  useQuery({
    queryKey: documentKeys.changes(documentId, fromDate, toDate),

    queryFn: async () => {
      const { data } = await api.GET('/documents/{superId}/changes', {
        params: { path: { superId: Number(documentId) }, query: { fromDate: String(fromDate), toDate: String(toDate) } },
      });
      return data;
    },

    enabled: !!documentId,
  });

export const useSectionRevision = (sectionRevisionId: string) =>
  useQuery({
    queryKey: documentKeys.sectionRevision(sectionRevisionId),

    queryFn: async () => {
      const { data } = await api.GET('/sections/{revisionId}', { params: { path: { revisionId: sectionRevisionId } } });
      return data;
    },

    enabled: !!sectionRevisionId,
  });

export const useSimpleVersions = (documentId: number | string) =>
  useQuery({
    queryKey: documentKeys.simpleVersions(documentId),

    queryFn: async () => {
      const { data } = await api.GET('/versions/{documentSuperId}', { params: { path: { documentSuperId: Number(documentId) } } });
      return data;
    },

    enabled: !!documentId,
  });

export const useCreateVersion = () =>
  useMutation({
    mutationFn: ({
      documentId,
      documentSuperId,
      documentRevisionId,
      name,
    }: {
      documentId: number | string;
      documentSuperId: number | string;
      documentRevisionId?: string;
      name: string;
    }) =>
      api.POST('/versions/{documentSuperId}', {
        params: { path: { documentSuperId: Number(documentId) } },
        body: { documentSuperId: Number(documentSuperId), documentRevisionId: documentRevisionId as string, name },
      }),

    onError: ServerErrorHandler,
  });
